import { HttpClient, HttpParams } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable } from "rxjs/internal/Observable";
import { environment } from "src/environments/environment";
import { productCategories, ProductAttributes, BaseProduct } from "@models/product.model";
import { BehaviorSubject } from "rxjs";
import { MeshType, ShapeCounts, ShapeData, ShapeInfo } from "../../../utils/shape-type";
import { CountOutputData } from "../../../../../utils/count-calculator/count-calculator.model";
import { DiscountRange } from "@models/product.model";
import { BaseReview, ReviewAttributes } from "@models/review.model";
import { ModelQuantityJson } from "@models/structure.model";
@Injectable({
    providedIn: "root",
})
export class ProductService {
    private dataSubject = new BehaviorSubject<string>("");
    productName$ = this.dataSubject.asObservable();

    constructor(private http: HttpClient) {}

    public setProductName(data: string) {
        this.dataSubject.next(data);
    }

    public getProductName(): string {
        return this.dataSubject.value;
    }

    public getAllActiveProducts(
        pageIndex: number = environment.dynamicPagination.pageIndex,
        pageSize: number = environment.dynamicPagination.pageSize,
        searchInput?: string,
        category?: string,
        selectedSubCategories?: string[],
        minPrice?: number,
        maxPrice?: number,
        selectedSort?: string,
        offerCategory?: string,
    ): Observable<{ count: number; rows: ProductAttributes[] }> {
        const subCategoriesParam = selectedSubCategories?.join(",") ?? "";

        let url = `${environment.apiUrlBasePath}/products/getAllActive?pageIndex=${pageIndex}&pageSize=${pageSize}`;

        if (searchInput) {
            const encodedProductName = encodeURIComponent(searchInput);
            url += `&searchInput=${encodedProductName}`;
        }

        if (category) {
            url += `&category=${category}`;
        }

        if (subCategoriesParam) {
            url += `&selectedSubCategories=${subCategoriesParam}`;
        }

        if (selectedSort) {
            url += `&selectedSort=${selectedSort}`;
        }

        if (minPrice) {
            url += `&minPrice=${minPrice}`;
        }

        if (maxPrice) {
            url += `&maxPrice=${maxPrice}`;
        }

        if (offerCategory) {
            url += `&offerCategory=${offerCategory}`;
        }

        return this.http.get<{ count: number; rows: ProductAttributes[] }>(url);
    }

    public getAllProducts(
        pageIndex: number = environment.dynamicPagination.pageIndex,
        pageSize: number = environment.dynamicPagination.pageSize,
        name?: string,
    ): Observable<{ count: number; rows: ProductAttributes[] }> {
        let url = "";
        if (name) {
            const encodedProductName = encodeURIComponent(name);
            url = `${environment.apiUrlBasePath}/admin/getAll/products?pageIndex=${pageIndex}&pageSize=${pageSize}&name=${encodedProductName}`;
        } else {
            url = `${environment.apiUrlBasePath}/admin/getAll/products?pageIndex=${pageIndex}&pageSize=${pageSize}`;
        }

        return this.http.get<{ count: number; rows: ProductAttributes[] }>(url);
    }

    public addProduct(product: BaseProduct): Observable<{ product: ProductAttributes; message: string }> {
        const url = `${environment.apiUrlBasePath}/admin/addProduct`;
        return this.http.post<{ product: ProductAttributes; message: string }>(url, product);
    }

    public getProductById(id: number, allowUnavilableProduct: boolean = false): Observable<ProductAttributes> {
        const url = `${environment.apiUrlBasePath}/products/getProductById/${id}`;
        let params = new HttpParams();
        params = params.set("allowUnavilableProduct", allowUnavilableProduct);
        return this.http.get<ProductAttributes>(url, { params });
    }

    public editProduct(
        productId: number,
        updatedProductData: Partial<BaseProduct>,
    ): Observable<{ product: BaseProduct; message: string }> {
        const url = `${environment.apiUrlBasePath}/admin/editProduct/${productId}`;
        return this.http.put<{ product: ProductAttributes; message: string }>(url, updatedProductData);
    }

    public deleteFile(fileUrl: string): Observable<{ message: string }> {
        const url = `${environment.apiUrlBasePath}/common/deleteFile`;
        return this.http.post<{ message: string }>(`${url}`, { fileUrl });
    }

    public deleteFiles(fileUrls: string[]): Observable<{ message: string }> {
        const url = `${environment.apiUrlBasePath}/common/deleteFiles`;
        return this.http.post<{ message: string }>(`${url}`, { fileUrls });
    }

    public getInActiveProductIds(): Observable<number[]> {
        return this.http.get<number[]>(`${environment.apiUrlBasePath}/products/getInactive`);
    }

    public addReview(review: BaseReview): Observable<{ message: string }> {
        return this.http.post<{ message: string }>(`${environment.apiUrlBasePath}/products/addRateAndReview`, review);
    }

    public suggestedProduct(subCategory: string, productId: number): Observable<ProductAttributes[]> {
        return this.http.get<ProductAttributes[]>(
            `${environment.apiUrlBasePath}/products/suggestedProduct?subCategory=${subCategory}&productId=${productId}`,
        );
    }

    public animatePlaceholder(inputElement: HTMLInputElement, placeholderText: string): void {
        const typingSpeed: number = 100;
        const erasingSpeed: number = 50;
        const pauseDuration: number = 1000;

        let i: number = 0;
        let isErasing: boolean = false;

        function typeEffect(): void {
            if (isErasing) {
                if (i > 0) {
                    inputElement.placeholder = placeholderText.slice(0, i - 1);
                    i--;
                    setTimeout(typeEffect, erasingSpeed);
                } else {
                    isErasing = false;
                    setTimeout(typeEffect, pauseDuration);
                }
            } else {
                if (i < placeholderText.length) {
                    inputElement.placeholder = placeholderText.slice(0, i + 1);
                    i++;
                    setTimeout(typeEffect, typingSpeed);
                } else {
                    isErasing = true;
                    setTimeout(typeEffect, pauseDuration);
                }
            }
        }

        typeEffect();
    }

    private selectedCategory: string = productCategories[0].toString();
    setSelectedCategory(category: string): void {
        this.selectedCategory = category;
    }

    getSelectedCategory(): string {
        return this.selectedCategory;
    }

    private resetSubject = new BehaviorSubject<boolean>(false);
    reset = this.resetSubject.asObservable();

    triggerReset(reset: boolean) {
        this.resetSubject.next(reset);
    }

    public calculateShapeInfo(model: ModelQuantityJson): ShapeInfo {
        const shapeInformation = model["faceinformation"];
        const totalBlock = model["totalcount"];
        const shapeCounts: ShapeCounts = Object.values(MeshType).reduce((acc, shape) => {
            acc[shape] = 0;
            return acc;
        }, {} as ShapeCounts);

        shapeInformation.forEach((shape: CountOutputData) => {
            if (shape.name in shapeCounts) {
                shapeCounts[shape.name]++;
            }
        });

        const shapeArray = Object.keys(shapeCounts).map((key) => ({
            name: key,
            count: shapeCounts[key],
        }));

        const filteredShapeArray: ShapeData[] = shapeArray.filter((shape) => shape.count > 0);
        return { filteredShapeArray, totalBlock };
    }

    public getDiscountRanges(): Observable<DiscountRange[]> {
        const url = `${environment.apiUrlBasePath}/products/getDiscountRanges`;
        return this.http.get<DiscountRange[]>(url);
    }

    public getAllReviews(
        productId: number,
        pageIndex: number = environment.dynamicPagination.pageIndex,
        pageSize: number = environment.dynamicPagination.pageSize,
    ): Observable<{ count: number; rows: ReviewAttributes[] }> {
        const url = `${environment.apiUrlBasePath}/products/getReviews/${productId}?pageIndex=${pageIndex}&pageSize=${pageSize}`;
        return this.http.get<{ count: number; rows: ReviewAttributes[] }>(url);
    }

    public getReviewsDistribution(productId: number): Observable<any> {
        const url = `${environment.apiUrlBasePath}/products/reviews/distribution/${productId}`;
        return this.http.get<any>(url);
    }

    // calling the google geocoding api to get the pincode
    public getPincodeforGuestUser(lat: number, lon: number): Observable<string> {
        const url = `${environment.apiUrlBasePath}/location/pincode?lat=${lat}&lon=${lon}`;
        return this.http.get<string>(url);
    }

    public getAvailableSubCategories(
        minPrice: number,
        maxPrice: number,
        category?: string,
    ): Observable<(string | undefined)[]> {
        let url = `${environment.apiUrlBasePath}/products/availableSubCategories?minPrice=${minPrice}&maxPrice=${maxPrice}`;
        if (category) {
            url += `&category=${category}`;
        }
        return this.http.get<(string | undefined)[]>(url);
    }
}
